import type { DateRange } from './dateRange';
import { post } from './http';

/**
 * The five pages added after Availability — Sales, Actions, PO Modules,
 * Visibility and Discount — and the one request each of them makes.
 *
 * Kept apart from `api.ts`, which is Availability's, because these share a
 * request envelope Availability does not: one window, one filter object, one
 * tenant, and a `Cell` on every figure that carries its own comparison. Adding
 * them there would have meant one module speaking two conventions.
 *
 * The envelope is what differs; the wire is not. Both go out through
 * `http.ts`, which is also what notices an expired session.
 */

/** Kept as a name so callers can still catch it; the API throws one class. */
export { ApiError, ApiError as PageApiError } from './http';

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/** A figure with its previous-period comparison. Mirrors the API's `Cell`. */
export interface Cell {
  value: number | null;
  previous: number | null;
  delta: number | null;
  delta_pct: number | null;
  unavailable_reason?: string;
}

export interface ResolvedWindow {
  start: string;
  end: string;
  previous_start?: string;
  previous_end?: string;
  as_of: string;
  /** Why the window is not what was asked for. Rendered by PageShell. */
  note?: string;
  /** Days of history this page's source holds. */
  retention_days?: number;
  /** True when `start` was pulled forward to the source's retention floor. */
  truncated?: boolean;
  /**
   * False when the previous period falls outside what the source holds. Every
   * delta on the page is null in that case — by design, not by accident.
   */
  comparison_available?: boolean;
}

export interface ScopeFilters {
  category?: string;
  city?: string;
  sku?: string;
}

interface BaseRequest {
  customer_name: string;
  window: DateRange;
}

// ---------------------------------------------------------------------------
// Sales
// ---------------------------------------------------------------------------

export interface SalesRow {
  id: string;
  label: string;
  value?: string;
  entityId?: string;
  sales: Cell;
  units: Cell;
  units_per_day: Cell;
  asp: Cell;
  discount_pct: Cell;
  wt_osa: Cell;
  share_pct: number | null;
  sku_count?: number | null;
  series: (number | null)[] | null;
  ads?: {
    associated_spend: number | null;
    associated_sales: number | null;
    associated_roas: number | null;
    association: true;
  };
}

export interface SalesPoint {
  day: string;
  sales_actual: number | null;
  sales_est: number | null;
  units_actual: number | null;
  units_est: number | null;
  ad_spend: number | null;
  ad_sales: number | null;
}

export interface CampaignRow {
  campaign_id: number;
  campaign_name: string;
  campaign_type: string | null;
  spend: number | null;
  ad_sales: number | null;
  roas: number | null;
  acos: number | null;
  impressions: number | null;
  units: number | null;
  spend_share_pct: number | null;
}

export interface Mover {
  grain: 'city' | 'category' | 'sku';
  id: string;
  label: string;
  value?: string;
  sales: number | null;
  previous: number | null;
  delta: number | null;
  delta_pct: number | null;
}

export interface SalesOverview {
  filters: ScopeFilters;
  window: ResolvedWindow;
  basis: 'actual' | 'estimated';
  actuals_through: string | null;
  kpis: Record<
    | 'sales' | 'units' | 'units_per_day' | 'asp' | 'discount_pct' | 'wt_osa'
    | 'ad_spend' | 'ad_sales' | 'roas' | 'acos' | 'tacos' | 'sku_count' | 'campaigns',
    Cell
  >;
  series: SalesPoint[];
  tables: Record<'city' | 'category' | 'sku', { grain: string; rows: SalesRow[] }>;
  campaigns: CampaignRow[];
  movers: { gainers: Mover[]; decliners: Mover[] };
  response_meta: {
    bytes_scanned: number;
    queries: number;
    basis_note: string;
    ads_available: boolean;
  };
}

export function fetchSales(
  req: BaseRequest & { filters: ScopeFilters; limit?: number },
  signal?: AbortSignal,
): Promise<SalesOverview> {
  return post<SalesOverview>('/v1/sales/overview', req, signal);
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type ActionOwner = 'Brand' | 'Blinkit' | 'Shared';

export interface ActionRule {
  id: string;
  title: string;
  rule: string;
  owner: ActionOwner;
  team: string;
  source: string;
  value_label: string;
  value_is_association?: boolean;
}

export interface ActionItem {
  rule_id: string;
  key: string;
  title: string;
  subtitle: string | null;
  owner: ActionOwner;
  team: string;
  value_at_stake: number | null;
  value_label: string;
  severity: 1 | 2 | 3;
  scope: {
    city?: string | null;
    facility?: string | null;
    vendor?: string | null;
    product_id?: string | null;
    sku_name?: string | null;
    category?: string | null;
    po_number?: string | null;
  };
  evidence: { label: string; value: number | string | null; unit?: string }[];
  link?: { section: string; page: string; filters: Record<string, string> };
}

export interface ActionsFeed {
  filters: { city?: string; rule_id?: string; owner?: ActionOwner };
  window: ResolvedWindow;
  summary: {
    total_actions: number;
    shown_actions: number;
    /**
     * One total per KIND of money. There is deliberately no single figure
     * across kinds — revenue at risk, an unreceived order and blocked capital
     * are three different questions. `total` is null where the kind cannot be
     * summed at all (associated ad spend).
     */
    by_kind: { kind: string; label: string; actions: number; total: number | null; summable: boolean }[];
    by_owner: { owner: ActionOwner; actions: number }[];
    by_rule: { rule_id: string; title: string; actions: number; value_label: string; total: number | null }[];
  };
  rules: ActionRule[];
  actions: ActionItem[];
  response_meta: {
    bytes_scanned: number;
    queries: number;
    /** How each feed read the requested range — they differ by necessity. */
    windows: {
      requested_days: number;
      availability_days: number;
      advertising_days: number;
      purchase_orders_from: string;
      inventory_snapshot_on: string;
    };
    windows_note: string;
    sources: { name: string; ok: boolean; rows: number; note?: string }[];
    totals_note: string;
  };
}

export function fetchActions(
  req: BaseRequest & { filters: { city?: string; rule_id?: string; owner?: ActionOwner }; top?: number },
  signal?: AbortSignal,
): Promise<ActionsFeed> {
  return post<ActionsFeed>('/v1/actions/feed', req, signal);
}

// ---------------------------------------------------------------------------
// PO Modules
// ---------------------------------------------------------------------------

export interface PoLevelRow {
  id: string;
  label: string;
  value?: string;
  extra?: string | null;
  po_count: number | null;
  open_po_count: number | null;
  units_ordered: number | null;
  delivered_quantity: number | null;
  ordered_value: number | null;
  delivered_value: number | null;
  undelivered_value: number | null;
  open_undelivered_value: number | null;
  fill_rate: number | null;
  unfulfilled_lines: number | null;
  censored_lines: number | null;
  amended_lines: number | null;
  next_expiry_open: string | null;
}

export interface PurchaseOrder {
  po_number: number;
  city: string | null;
  state: string | null;
  facility_id: number | null;
  facility_name: string | null;
  vendor_name: string | null;
  order_date: string | null;
  expiry_date: string | null;
  appointment_date: string | null;
  close_date: string | null;
  po_state: string | null;
  po_lifecycle_status: string | null;
  po_window_days: number | null;
  units_ordered: number | null;
  delivered_quantity: number | null;
  remaining_quantity: number | null;
  ordered_value: number | null;
  delivered_value: number | null;
  undelivered_value: number | null;
  fill_rate: number | null;
  delivered_pct: number | null;
  open_lines: number | null;
  closed_lines: number | null;
  censored: boolean;
  days_to_expiry: number | null;
  unscheduled: boolean;
  amendments: { expiry: number | null; appointment: number | null };
}

export interface PoOverview {
  filters: { city?: string; vendor?: string };
  date_basis: 'order_date' | 'close_date';
  window: ResolvedWindow;
  kpis: Record<
    | 'fill_rate' | 'open_po_count' | 'open_undelivered_value' | 'ordered_value'
    | 'delivered_value' | 'undelivered_value' | 'unfulfilled_lines'
    | 'unscheduled_open_value' | 'expiring_soon_count' | 'expiring_soon_value'
    | 'amendments' | 'censored_lines',
    Cell
  >;
  levels: Record<'city' | 'facility' | 'vendor' | 'item', { level: string; rows: PoLevelRow[] }>;
  purchase_orders: PurchaseOrder[];
  response_meta: {
    bytes_scanned: number;
    queries: number;
    basis_note: string;
    fill_rate_note: string;
  };
}

export function fetchPo(
  req: BaseRequest & {
    filters: { city?: string; vendor?: string };
    date_basis?: 'order_date' | 'close_date';
    only_open?: boolean;
    po_limit?: number;
  },
  signal?: AbortSignal,
): Promise<PoOverview> {
  return post<PoOverview>('/v1/po/overview', req, signal);
}

// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------

export interface SovRow {
  id: string;
  label: string;
  value?: string;
  ad_sov: Cell;
  organic_sov: Cell;
  overall_sov: Cell;
  avg_ad_rank: Cell;
  avg_organic_rank: Cell;
  avg_overall_rank: Cell;
  appearances: number | null;
  search_volume?: number | null;
  screen_ad_share?: number | null;
}

export interface RivalRow {
  brand: string;
  is_own: boolean;
  ad_sov: Cell;
  organic_sov: Cell;
  overall_sov: Cell;
  appearances: number | null;
}

export interface SovProductRow {
  product_id: string;
  sku_name: string | null;
  brand: string | null;
  keywords: number | null;
  ad_sov: number | null;
  organic_sov: number | null;
  overall_sov: number | null;
  avg_ad_rank: number | null;
  avg_organic_rank: number | null;
  avg_overall_rank: number | null;
  appearances: number | null;
}

export interface SovPoint {
  day: string;
  ad_sov: number | null;
  organic_sov: number | null;
  overall_sov: number | null;
  avg_ad_rank: number | null;
  avg_organic_rank: number | null;
}

export interface VisibilityOverview {
  filters: { keyword?: string; city?: string; category?: string; slot?: 'morning' | 'evening' };
  window: ResolvedWindow;
  sov_source: 'hector_derived';
  kpis: Record<
    | 'overall_sov' | 'ad_sov' | 'organic_sov' | 'avg_ad_rank' | 'avg_organic_rank'
    | 'avg_overall_rank' | 'keywords_tracked' | 'cities_tracked' | 'appearances'
    | 'screen_ad_share',
    Cell
  >;
  series: SovPoint[];
  keywords: SovRow[];
  cities: SovRow[];
  slots: SovRow[];
  rivals: RivalRow[];
  products: SovProductRow[];
  response_meta: {
    bytes_scanned: number;
    queries: number;
    coverage: {
      keywords_in_scope: number;
      cities_in_scope: number;
      crawls: number;
      days_with_crawls: number;
      days_in_window: number;
    };
    sov_note: string;
    rivals_note: string;
  };
}

export function fetchVisibility(
  req: BaseRequest & {
    filters: { keyword?: string; city?: string; category?: string; slot?: 'morning' | 'evening' };
    limit?: number;
  },
  signal?: AbortSignal,
): Promise<VisibilityOverview> {
  return post<VisibilityOverview>('/v1/visibility/overview', req, signal);
}

// ---------------------------------------------------------------------------
// Discount
// ---------------------------------------------------------------------------

export interface DiscountRow {
  id: string;
  label: string;
  value?: string;
  entityId?: string;
  discount_pct: Cell;
  asp: Cell;
  avg_mrp: Cell;
  discount_value: Cell;
  units: Cell;
  sales: Cell;
  share_of_discount_pct: number | null;
  series?: (number | null)[] | null;
}

export interface DiscountHotspot {
  product_id: string;
  sku_name: string | null;
  city_name: string;
  l2_category_name: string | null;
  discount_pct: number | null;
  asp: number | null;
  avg_mrp: number | null;
  discount_value: number | null;
  sales: number | null;
  units: number | null;
  wt_osa: number | null;
}

export interface DiscountSpreadRow {
  product_id: string;
  sku_name: string | null;
  cities: number | null;
  min_discount_pct: number | null;
  max_discount_pct: number | null;
  spread_pct: number | null;
  deepest_city: string | null;
  shallowest_city: string | null;
  units: number | null;
  sales: number | null;
}

export interface DiscountPoint {
  day: string;
  discount_pct: number | null;
  asp: number | null;
  avg_mrp: number | null;
  discount_value: number | null;
  units: number | null;
}

export interface DiscountOverview {
  filters: ScopeFilters;
  window: ResolvedWindow;
  kpis: Record<
    | 'discount_pct' | 'discount_value' | 'asp' | 'avg_mrp' | 'sales' | 'units'
    | 'deep_discount_cells' | 'widest_spread_pct',
    Cell
  >;
  series: DiscountPoint[];
  tables: Record<'city' | 'category' | 'sku', { grain: string; rows: DiscountRow[] }>;
  hotspots: DiscountHotspot[];
  spread: DiscountSpreadRow[];
  response_meta: {
    bytes_scanned: number;
    queries: number;
    min_units: number;
    deep_discount_pct: number;
    basis_note: string;
    limitations: string;
  };
}

export function fetchDiscount(
  req: BaseRequest & { filters: ScopeFilters; limit?: number },
  signal?: AbortSignal,
): Promise<DiscountOverview> {
  return post<DiscountOverview>('/v1/discount/overview', req, signal);
}
